import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Pin, PinOff, Maximize2, ChevronDown, ChevronUp, FileText, Plus, Edit3, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import { Modal } from '../admin/Modal';
import { useNotesWindow, ExistingNoteData } from '../../contexts/NotesWindowContext';

// ============================================================================
// TYPES
// ============================================================================

interface NoteSection {
  content?: string;
  coach_observations?: string;
  client_visible?: boolean;
}

interface Note {
  note_id: string;
  title: string;
  session_date: string;
  created_at: string;
  created_by_name: string;
  signed_at: string | null;
  pinned: boolean;
  tags: string[];
  status: string;
  sections: {
    subjective?: NoteSection;
    objective?: NoteSection;
    assessment?: NoteSection;
    plan?: NoteSection;
  };
}

interface AllNotesWorkspaceProps {
  clientId: string;
  clientName?: string;
  onBack: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * AllNotesWorkspace - Full notes view for a client
 *
 * Replaces center+right columns when "All Notes" is clicked.
 * Shows complete list of all notes with:
 * - Same card format as pinned notes
 * - Expandable content
 * - Pop-out modal
 * - Pin/Unpin toggle
 */
export const AllNotesWorkspace: React.FC<AllNotesWorkspaceProps> = ({ clientId, clientName, onBack: _onBack }) => {
  const { openWindow, openWindowForEdit } = useNotesWindow();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [popoutNote, setPopoutNote] = useState<Note | null>(null);
  const [hoveredPinId, setHoveredPinId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showSignConfirm, setShowSignConfirm] = useState(false);
  const [noteToSign, setNoteToSign] = useState<Note | null>(null);
  const [signing, setSigning] = useState(false);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    loadAllNotes();
  }, [clientId]);

  const loadAllNotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notes/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Sort by session_date descending (most recent first), then by created_at for same-day notes
        const sorted = (data.notes || []).sort((a: Note, b: Note) => {
          const dateCompare = new Date(b.session_date).getTime() - new Date(a.session_date).getTime();
          if (dateCompare !== 0) return dateCompare;
          // Same session_date - sort by created_at (most recent first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        setNotes(sorted);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const toggleNoteExpanded = (noteId: string) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) {
        next.delete(noteId);
      } else {
        next.add(noteId);
      }
      return next;
    });
  };

  const handleTogglePin = async (noteId: string, currentlyPinned: boolean) => {
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = currentlyPinned ? 'unpin' : 'pin';
      const response = await fetch(`/api/coach/clients/${clientId}/notes/${noteId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Update local state
        setNotes(prev => prev.map(n =>
          n.note_id === noteId ? { ...n, pinned: !currentlyPinned } : n
        ));
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const handleNewNote = () => {
    openWindow(clientId, clientName || 'Unknown Client');
  };

  const handleEditNote = (note: Note) => {
    // Only allow editing draft notes
    if (note.signed_at) {
      return;
    }
    // Convert Note to ExistingNoteData format
    const existingNoteData: ExistingNoteData = {
      note_id: note.note_id,
      title: note.title,
      session_date: note.session_date,
      tags: note.tags,
      sections: note.sections
    };
    openWindowForEdit(clientId, clientName || 'Unknown Client', existingNoteData);
  };

  // Listen for notes-updated event to refresh the list
  useEffect(() => {
    const handleNotesUpdated = (e: CustomEvent<{ clientId: string }>) => {
      if (e.detail.clientId === clientId) {
        loadAllNotes();
      }
    };
    window.addEventListener('notes-updated', handleNotesUpdated as EventListener);
    return () => {
      window.removeEventListener('notes-updated', handleNotesUpdated as EventListener);
    };
  }, [clientId]);

  const handleDeleteClick = (note: Note) => {
    // Only allow deleting draft/unsigned notes
    if (note.signed_at) {
      return;
    }
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notes/${clientId}/${noteToDelete.note_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Remove from local state
        setNotes(prev => prev.filter(n => n.note_id !== noteToDelete.note_id));
        setShowDeleteConfirm(false);
        setNoteToDelete(null);
      } else {
        const data = await response.json();
        console.error('Delete failed:', data.error);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleSignClick = (note: Note) => {
    // Only allow signing draft/unsigned notes
    if (note.signed_at) {
      return;
    }
    setNoteToSign(note);
    setShowSignConfirm(true);
  };

  const handleConfirmSign = async () => {
    if (!noteToSign) return;

    setSigning(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notes/${clientId}/${noteToSign.note_id}/sign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const signedData = await response.json();

        // Update local state - mark as signed
        setNotes(prev => prev.map(n =>
          n.note_id === noteToSign.note_id
            ? { ...n, signed_at: new Date().toISOString(), status: 'signed' }
            : n
        ));
        setShowSignConfirm(false);
        setNoteToSign(null);
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('notes-updated', { detail: { clientId } }));

        // Standard Email Feedback Contract — toast for workflow email result
        if (signedData.emailSent === true) {
          toast.success('Notes signed and emailed to client');
        } else if (signedData.emailSent === false) {
          toast.error(`Notes signed. Email delivery failed: ${signedData.emailError || 'Unknown error'}`);
        }
      } else {
        const data = await response.json();
        console.error('Sign failed:', data.error);
        alert(`Failed to sign note: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error signing note:', error);
      alert('Failed to sign note. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const buildFullContent = (sections: Note['sections']) => {
    const parts: string[] = [];

    if (sections?.subjective?.content) {
      parts.push('## Subjective');
      parts.push(sections.subjective.content);
    }
    if (sections?.objective?.coach_observations) {
      parts.push('## Observations');
      parts.push(sections.objective.coach_observations);
    }
    if (sections?.assessment?.content) {
      parts.push('## Assessment');
      parts.push(sections.assessment.content);
    }
    if (sections?.plan?.content) {
      parts.push('## Plan');
      parts.push(sections.plan.content);
    }

    return parts.join('\n\n');
  };

  const buildPreview = (sections: Note['sections']) => {
    if (sections?.assessment?.content) {
      const content = sections.assessment.content;
      return content.length > 200 ? content.substring(0, 197) + '...' : content;
    }
    if (sections?.plan?.content) {
      const content = sections.plan.content;
      return content.length > 200 ? content.substring(0, 197) + '...' : content;
    }
    if (sections?.subjective?.content) {
      const content = sections.subjective.content;
      return content.length > 200 ? content.substring(0, 197) + '...' : content;
    }
    return 'No content preview available';
  };

  const renderMarkdown = (text: string) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inList = false;
    let listItems: string[] = [];

    const processInlineFormatting = (line: string): React.ReactNode => {
      let remaining = line;
      remaining = remaining.replace(/\*\*(.+?)\*\*|__(.+?)__/g, (_, g1, g2) => `<b>${g1 || g2}</b>`);
      remaining = remaining.replace(/(?<!<b>)\*(.+?)\*(?!<\/b>)|(?<!<b>)_(.+?)_(?!<\/b>)/g,
        (_, g1, g2) => `<i>${g1 || g2}</i>`);

      const htmlPattern = /<(b|i)>(.+?)<\/\1>/g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;
      let key = 0;

      while ((match = htmlPattern.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
          parts.push(remaining.slice(lastIndex, match.index));
        }
        if (match[1] === 'b') {
          parts.push(<strong key={key++}>{match[2]}</strong>);
        } else {
          parts.push(<em key={key++}>{match[2]}</em>);
        }
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < remaining.length) {
        parts.push(remaining.slice(lastIndex));
      }

      return parts.length > 0 ? parts : remaining;
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems.map((item, i) => (
              <li key={i} style={{ marginBottom: '4px' }}>{processInlineFormatting(item)}</li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      const bulletMatch = line.match(/^[\s]*[-*]\s+(.+)/);
      if (bulletMatch) {
        inList = true;
        listItems.push(bulletMatch[1]);
        return;
      } else if (inList) {
        flushList();
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h4 key={index} style={{ fontSize: '14px', fontWeight: 600, margin: '12px 0 8px' }}>
            {processInlineFormatting(line.slice(4))}
          </h4>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h3 key={index} style={{ fontSize: '15px', fontWeight: 600, margin: '12px 0 8px' }}>
            {processInlineFormatting(line.slice(3))}
          </h3>
        );
      } else if (line.startsWith('# ')) {
        elements.push(
          <h2 key={index} style={{ fontSize: '16px', fontWeight: 600, margin: '12px 0 8px' }}>
            {processInlineFormatting(line.slice(2))}
          </h2>
        );
      } else if (line.trim() === '') {
        elements.push(<div key={index} style={{ height: '8px' }} />);
      } else {
        elements.push(
          <p key={index} style={{ margin: '4px 0', lineHeight: 1.5 }}>
            {processInlineFormatting(line)}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <div className="all-notes-workspace">
        {/* Header */}
        <div className="workspace-header">
          <h2><FileText size={20} /> All Notes</h2>
          <button className="btn-primary" onClick={handleNewNote}>
            <Plus size={14} />
            New Note
          </button>
        </div>

        {/* Notes List */}
        <div className="notes-container">
          {loading ? (
            <div className="loading-state">
              <p>Loading notes...</p>
            </div>
          ) : notes.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <h3>No Notes Yet</h3>
              <p>Create a new session note to get started</p>
            </div>
          ) : (
            <div className="notes-list">
              {notes.map(note => {
                const isExpanded = expandedNotes.has(note.note_id);
                const preview = buildPreview(note.sections);

                return (
                  <div key={note.note_id} className={`note-card ${note.status === 'draft' ? 'draft' : ''} ${note.signed_at ? 'signed' : ''}`}>
                    <div className="note-header">
                      <div className="note-title-row">
                        <span className="note-title">{note.title}</span>
                        <div className="note-badges">
                          {note.status === 'draft' && (
                            <span className="badge badge-draft">Draft</span>
                          )}
                          {note.signed_at && (
                            <span className="badge badge-signed">Signed</span>
                          )}
                          {note.pinned && (
                            <span className="badge badge-pinned">Pinned</span>
                          )}
                        </div>
                      </div>
                      <div className="note-actions">
                        {/* Edit button - only for drafts */}
                        {!note.signed_at && (
                          <button
                            className="icon-btn"
                            onClick={() => handleEditNote(note)}
                            title="Edit note"
                          >
                            <Edit3 size={14} />
                          </button>
                        )}
                        {/* Delete button - only for drafts */}
                        {!note.signed_at && (
                          <button
                            className="icon-btn delete-btn"
                            onClick={() => handleDeleteClick(note)}
                            title="Delete draft"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                        {/* Sign button - only for drafts */}
                        {!note.signed_at && (
                          <button
                            className="icon-btn sign-btn"
                            onClick={() => handleSignClick(note)}
                            title="Sign & finalize"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        <button
                          className="icon-btn"
                          onClick={() => setPopoutNote(note)}
                          title="Pop-out"
                        >
                          <Maximize2 size={14} />
                        </button>
                        <button
                          className={`icon-btn pin-btn ${note.pinned ? 'pinned' : ''}`}
                          onClick={() => handleTogglePin(note.note_id, note.pinned)}
                          onMouseEnter={() => setHoveredPinId(note.note_id)}
                          onMouseLeave={() => setHoveredPinId(null)}
                          title={note.pinned ? 'Unpin' : 'Pin'}
                        >
                          {/* Show PinOff when: pinned AND hovering (about to unpin) */}
                          {/* Show Pin when: not pinned OR pinned but not hovering */}
                          {note.pinned && hoveredPinId === note.note_id ? <PinOff size={14} /> : <Pin size={14} />}
                        </button>
                        <button
                          className="expand-btn"
                          onClick={() => toggleNoteExpanded(note.note_id)}
                        >
                          {isExpanded ? <>Collapse <ChevronUp size={14} /></> : <>Expand <ChevronDown size={14} /></>}
                        </button>
                      </div>
                    </div>

                    <div className="note-date">
                      {note.session_date}
                    </div>

                    <div className="note-content">
                      <div className="markdown-content">
                        {isExpanded
                          ? renderMarkdown(buildFullContent(note.sections))
                          : renderMarkdown(preview)
                        }
                      </div>
                    </div>

                    <div className="note-meta">
                      <span>By {note.created_by_name}</span>
                      {note.tags.length > 0 && (
                        <span className="note-tags">
                          {note.tags.map(tag => (
                            <span key={tag} className="note-tag">{tag}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Pop-out Modal */}
      <Modal
        isOpen={!!popoutNote}
        onClose={() => setPopoutNote(null)}
        title={popoutNote?.title || 'Note'}
        variant="info"
        size="large"
        footer={
          <button className="btn btn-secondary" onClick={() => setPopoutNote(null)}>
            Close
          </button>
        }
      >
        {popoutNote && (
          <>
            <div className="note-modal-meta">
              <span className="note-date-badge">{popoutNote.session_date}</span>
              {popoutNote.signed_at && (
                <span className="badge badge-signed">Signed</span>
              )}
              {popoutNote.pinned && (
                <span className="badge badge-pinned">Pinned</span>
              )}
              {popoutNote.tags.map(tag => (
                <span key={tag} className="note-tag">{tag}</span>
              ))}
            </div>
            <div className="markdown-content popout-content">
              {renderMarkdown(buildFullContent(popoutNote.sections))}
            </div>
            <div className="content-meta" style={{ marginTop: '16px' }}>
              By {popoutNote.created_by_name}
              {popoutNote.signed_at && ` • Signed ${new Date(popoutNote.signed_at).toLocaleDateString()}`}
            </div>
          </>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setNoteToDelete(null);
        }}
        title="Delete Draft Note"
        variant="danger"
        size="small"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setNoteToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              <Trash2 size={14} />
              {deleting ? 'Deleting...' : 'Delete Note'}
            </button>
          </>
        }
      >
        <div className="delete-confirm-content">
          <p>Are you sure you want to delete this draft note?</p>
          {noteToDelete && (
            <div className="note-preview">
              <strong>{noteToDelete.title || 'Untitled'}</strong>
              <span className="note-date">{noteToDelete.session_date}</span>
            </div>
          )}
          <p className="warning-text">This action cannot be undone.</p>
        </div>
      </Modal>

      {/* Sign Confirmation Modal */}
      <Modal
        isOpen={showSignConfirm}
        onClose={() => {
          setShowSignConfirm(false);
          setNoteToSign(null);
        }}
        title="Sign & Finalize Note"
        variant="warning"
        size="small"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowSignConfirm(false);
                setNoteToSign(null);
              }}
              disabled={signing}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirmSign}
              disabled={signing}
            >
              <CheckCircle size={14} />
              {signing ? 'Signing...' : 'Sign Note'}
            </button>
          </>
        }
      >
        <div className="sign-confirm-content">
          <p>Are you sure you want to sign and finalize this note?</p>
          {noteToSign && (
            <div className="note-preview">
              <strong>{noteToSign.title || 'Untitled'}</strong>
              <span className="note-date">{noteToSign.session_date}</span>
            </div>
          )}
          <div className="warning-box">
            <AlertCircle size={16} />
            <div>
              <strong>This action is permanent:</strong>
              <ul>
                <li>The note will become read-only</li>
                <li>It cannot be edited or deleted after signing</li>
                <li>Client-visible sections will become visible to the client</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        .all-notes-workspace {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 0 24px 24px;
        }

        .workspace-header {
          display: flex;
          align-items: center;
          gap: 24px;
          padding: 16px 0;
          border-bottom: 1px solid var(--border-secondary);
          margin-bottom: 24px;
        }

        .workspace-header h2 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 20px;
          font-weight: 600;
          color: var(--text-primary);
          flex: 1;
          margin: 0;
        }

        .workspace-header .back-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .workspace-header .back-btn:hover {
          background: var(--bg-primary);
          border-color: var(--accent-blue);
          color: var(--accent-blue);
        }

        .workspace-header .btn-primary {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: var(--accent-green);
          border: none;
          border-radius: var(--radius-medium);
          color: white;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .workspace-header .btn-primary:hover {
          background: var(--accent-green-hover);
        }

        .notes-container {
          flex: 1;
          overflow-y: auto;
        }

        .loading-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px;
          text-align: center;
          color: var(--text-secondary);
        }

        .empty-state svg {
          opacity: 0.3;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px;
          font-size: 18px;
          color: var(--text-primary);
        }

        .empty-state p {
          margin: 0;
          font-size: 14px;
        }

        .notes-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .note-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          padding: 16px;
          transition: all 0.15s;
        }

        .note-card:hover {
          border-color: var(--border-primary);
        }

        .note-card.draft {
          border-left: 3px solid var(--accent-yellow);
        }

        .note-card.signed {
          border-left: 3px solid var(--accent-green);
        }

        .note-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }

        .note-title-row {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .note-title {
          font-weight: 600;
          font-size: 16px;
          color: var(--text-primary);
        }

        .note-badges {
          display: flex;
          gap: 6px;
        }

        .badge {
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }

        .badge-draft {
          background: var(--accent-yellow-bg);
          color: var(--accent-yellow);
        }

        .badge-signed {
          background: var(--accent-green-bg);
          color: var(--accent-green);
        }

        .badge-pinned {
          background: var(--accent-blue-bg);
          color: var(--accent-blue);
        }

        .note-date {
          font-size: 12px;
          color: var(--text-tertiary);
          margin-bottom: 12px;
        }

        .note-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s;
        }

        .icon-btn:hover {
          background: var(--bg-primary);
          border-color: var(--accent-blue);
          color: var(--accent-blue);
        }

        /* Pin button - unpinned state (default) */
        .icon-btn.pin-btn {
          color: var(--text-tertiary);
          border-color: var(--border-secondary);
          background: transparent;
        }

        /* Pin button - hover on unpinned (ready to pin) */
        .icon-btn.pin-btn:hover {
          color: white;
          border-color: white;
          background: var(--accent-green);
        }

        /* Pin button - pinned state */
        .icon-btn.pin-btn.pinned {
          color: white;
          border-color: white;
          background: var(--accent-green);
        }

        /* Pin button - hover on pinned (ready to unpin) */
        .icon-btn.pin-btn.pinned:hover {
          color: white;
          border-color: white;
          background: var(--accent-red, #e53e3e);
        }

        /* Delete button - hover shows red */
        .icon-btn.delete-btn:hover {
          background: var(--accent-red, #e53e3e);
          color: white;
          border-color: var(--accent-red, #e53e3e);
        }

        /* Delete confirmation modal content */
        .delete-confirm-content {
          text-align: center;
        }

        .delete-confirm-content p {
          margin: 0 0 16px;
          color: var(--text-primary);
        }

        .delete-confirm-content .note-preview {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          padding: 12px;
          margin-bottom: 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .delete-confirm-content .note-preview strong {
          color: var(--text-primary);
          font-size: 14px;
        }

        .delete-confirm-content .note-preview .note-date {
          color: var(--text-tertiary);
          font-size: 12px;
          margin: 0;
        }

        .delete-confirm-content .warning-text {
          color: var(--accent-red, #e53e3e);
          font-size: 13px;
          font-weight: 500;
        }

        /* Sign button - hover shows green */
        .icon-btn.sign-btn:hover {
          background: var(--accent-green);
          color: white;
          border-color: var(--accent-green);
        }

        /* Sign confirmation modal content */
        .sign-confirm-content {
          text-align: left;
        }

        .sign-confirm-content p {
          margin: 0 0 16px;
          color: var(--text-primary);
          text-align: center;
        }

        .sign-confirm-content .note-preview {
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          padding: 12px;
          margin-bottom: 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          text-align: center;
        }

        .sign-confirm-content .note-preview strong {
          color: var(--text-primary);
          font-size: 14px;
        }

        .sign-confirm-content .note-preview .note-date {
          color: var(--text-tertiary);
          font-size: 12px;
          margin: 0;
        }

        .sign-confirm-content .warning-box {
          display: flex;
          gap: 12px;
          padding: 12px;
          background: rgba(234, 179, 8, 0.1);
          border: 1px solid rgba(234, 179, 8, 0.3);
          border-radius: var(--radius-small);
        }

        .sign-confirm-content .warning-box svg {
          flex-shrink: 0;
          color: var(--accent-yellow);
          margin-top: 2px;
        }

        .sign-confirm-content .warning-box strong {
          display: block;
          margin-bottom: 8px;
          color: var(--text-primary);
          font-size: 13px;
        }

        .sign-confirm-content .warning-box ul {
          margin: 0;
          padding-left: 16px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .sign-confirm-content .warning-box li {
          margin-bottom: 4px;
        }

        .expand-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          color: var(--text-secondary);
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 6px 10px;
          border-radius: var(--radius-small);
        }

        .expand-btn:hover {
          color: var(--accent-green);
          background: var(--bg-primary);
        }

        .note-content {
          padding: 12px 0;
        }

        .markdown-content {
          color: var(--text-primary);
          font-size: 14px;
          line-height: 1.6;
        }

        .markdown-content strong {
          font-weight: 600;
          color: var(--accent-blue);
        }

        .note-meta {
          font-size: 12px;
          color: var(--text-tertiary);
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }

        .note-tags {
          display: flex;
          gap: 4px;
          margin-left: 8px;
        }

        .note-tag {
          background: var(--bg-primary);
          color: var(--text-secondary);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }

        /* Pop-out Modal */
        .popout-content {
          max-height: 60vh;
          overflow-y: auto;
        }

        .note-modal-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .note-date-badge {
          background: var(--bg-secondary);
          color: var(--text-secondary);
          padding: 4px 8px;
          border-radius: var(--radius-small);
          font-size: 12px;
          font-weight: 500;
        }

        .content-meta {
          font-size: 12px;
          color: var(--text-tertiary);
        }
      `}</style>
    </>
  );
};

export default AllNotesWorkspace;
